/**
 * tdesign v1.0.3
 * (c) 2023 tdesign
 * @license MIT
 */

import { _ as _objectWithoutProperties } from '../_chunks/dep-92728dc5.js';
import { _ as _defineProperty } from '../_chunks/dep-46de7b2e.js';
import { _ as _slicedToArray } from '../_chunks/dep-e43cf21c.js';
import React, { forwardRef, useRef, useEffect, useImperativeHandle } from 'react';
import { CSSTransition } from 'react-transition-group';
import classNames from 'classnames';
import { useLocaleReceiver } from '../locale/LocalReceiver.js';
import Portal from '../common/Portal.js';
import useSetState from '../_util/useSetState.js';
import useConfig from '../hooks/useConfig.js';
import { dialogDefaultProps } from './defaultProps.js';
import DialogCard from './DialogCard.js';
import useDialogEsc from './hooks/useDialogEsc.js';
import useDialogLockStyle from './hooks/useLockStyle.js';
import useDialogPosition from './hooks/useDialogPosition.js';
import useDialogDrag from './hooks/useDialogDrag.js';
import { parseValueToPx } from './utils.js';
import '../_common/js/log/index.js';
import log from '../_common/js/log/log.js';
import '../_chunks/dep-2acfafca.js';
import '../config-provider/index.js';
import '../config-provider/ConfigProvider.js';
import '../_chunks/dep-c3f1141e.js';
import '../_chunks/dep-d71ee5f3.js';
import '../_chunks/dep-4b8f240e.js';
import '../_chunks/dep-9acbc163.js';
import '../_chunks/dep-7b52f080.js';
import '../_chunks/dep-f10515e8.js';
import '../_chunks/dep-4941ebe7.js';
import '../_chunks/dep-e9b62d1f.js';
import '../_chunks/dep-88cd50bf.js';
import '../_chunks/dep-dcb5aa2a.js';
import '../_chunks/dep-ee4bf8c2.js';
import '../_chunks/dep-934eda0c.js';
import '../_chunks/dep-7dce2c26.js';
import '../_chunks/dep-12510d64.js';
import '../_chunks/dep-6b568d83.js';
import '../_chunks/dep-304e657b.js';
import '../_chunks/dep-77e34309.js';
import '../_chunks/dep-6506fbc2.js';
import '../_chunks/dep-bb5d7b1e.js';
import '../_chunks/dep-e285f2f8.js';
import '../_chunks/dep-f4a320cd.js';
import '../_chunks/dep-ec70b95c.js';
import '../config-provider/ConfigContext.js';
import '../locale/zh_CN.js';
import '../_common/js/global-config/locale/zh_CN.js';
import 'dayjs';
import '../_chunks/dep-cbcc9f89.js';
import '../_common/js/global-config/default-config.js';
import 'react-dom';
import '../_util/dom.js';
import 'raf';
import '../_chunks/dep-6c5254c6.js';
import '../_util/easing.js';
import 'tdesign-icons-react';
import '../button/index.js';
import '../button/Button.js';
import '../hooks/useDomRefCallback.js';
import '../_util/useRipple.js';
import '../_util/useAnimation.js';
import '../_util/setStyle.js';
import '../loading/index.js';
import '../loading/Loading.js';
import '../loading/gradient.js';
import '../_common/js/loading/circle-adapter.js';
import '../_common/js/utils/set-style.js';
import '../_common/js/utils/helper.js';
import '../_chunks/dep-fd3498b6.js';
import '../_chunks/dep-ec16db36.js';
import '../_chunks/dep-036ad2cc.js';
import '../_chunks/dep-0e15088f.js';
import '../loading/defaultProps.js';
import '../loading/plugin.js';
import '../_util/react-render.js';
import '../_chunks/dep-e0370ebe.js';
import '../loading/style/css.js';
import '../loading/type.js';
import '../button/defaultProps.js';
import '../_util/parseTNode.js';
import '../button/style/css.js';
import '../button/type.js';
import '../hooks/useGlobalIcon.js';
import '../_util/useLayoutEffect.js';
import '../_common/js/utils/getScrollbarWidth.js';

var _excluded = ["style", "width", "mode", "zIndex", "visible", "attach", "onOpened", "onCancel", "onConfirm", "onClose", "onClosed", "isPlugin", "draggable", "onOverlayClick", "onEscKeydown", "closeOnEscKeydown", "confirmOnEnter", "showOverlay", "showInAttachedElement", "closeOnOverlayClick", "destroyOnClose", "preventScrollThrough"];
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
var Dialog = /*#__PURE__*/forwardRef(function (props, ref) {
  var _classNames, _classNames2;
  var _useConfig = useConfig(),
    classPrefix = _useConfig.classPrefix;
  var componentCls = "".concat(classPrefix, "-dialog");
  var wrapRef = useRef();
  var maskRef = useRef();
  var contentClickRef = useRef(false);
  var dialogCardRef = useRef();
  var dialogPosition = useRef();
  var portalRef = useRef();
  var _useSetState = useSetState(_objectSpread({
      isPlugin: false
    }, props)),
    _useSetState2 = _slicedToArray(_useSetState, 2),
    state = _useSetState2[0],
    setState = _useSetState2[1];
  var _useLocaleReceiver = useLocaleReceiver("dialog"),
    _useLocaleReceiver2 = _slicedToArray(_useLocaleReceiver, 1),
    local = _useLocaleReceiver2[0];
  var style = state.style,
    width = state.width,
    mode = state.mode,
    zIndex = state.zIndex,
    visible = state.visible,
    attach = state.attach,
    onOpened = state.onOpened,
    onCancel = state.onCancel,
    onConfirm = state.onConfirm,
    onClose = state.onClose,
    onClosed = state.onClosed,
    isPlugin = state.isPlugin,
    draggable = state.draggable,
    onOverlayClick = state.onOverlayClick,
    onEscKeydown = state.onEscKeydown,
    closeOnEscKeydown = state.closeOnEscKeydown,
    confirmOnEnter = state.confirmOnEnter,
    showOverlay = state.showOverlay,
    showInAttachedElement = state.showInAttachedElement,
    closeOnOverlayClick = state.closeOnOverlayClick,
    destroyOnClose = state.destroyOnClose,
    preventScrollThrough = state.preventScrollThrough,
    restState = _objectWithoutProperties(state, _excluded);
  useDialogLockStyle({
    preventScrollThrough: preventScrollThrough,
    visible: visible,
    mode: mode,
    showInAttachedElement: showInAttachedElement
  });
  useDialogEsc(visible, wrapRef);
  useDialogPosition(visible, dialogCardRef);
  var _useDialogDrag = useDialogDrag({
      dialogCardRef: dialogCardRef,
      contentClickRef: contentClickRef,
      canDraggable: draggable && mode === "modeless"
    }),
    onDialogMoveStart = _useDialogDrag.onDialogMoveStart;
  useEffect(function () {
    if (isPlugin) return;
    setState(function (prevState) {
      return _objectSpread(_objectSpread({}, prevState), props);
    });
  }, [props, setState, isPlugin]);
  useImperativeHandle(ref, function () {
    return {
      show: function show() {
        setState({
          visible: true
        });
      },
      hide: function hide() {
        setState({
          visible: false
        });
      },
      destroy: function destroy() {
        setState({
          visible: false,
          destroyOnClose: true
        });
      },
      update: function update(newOptions) {
        setState(function (prevState) {
          return _objectSpread(_objectSpread({}, prevState), newOptions);
        });
      }
    };
  });
  if (props.mode === "normal") {
    log.error("Dialog", 'mode="normal" is not supported, please use DialogCard.');
    return /* @__PURE__ */React.createElement(DialogCard, _objectSpread({}, props));
  }
  var onMaskClick = function onMaskClick(e) {
    if (showOverlay && (closeOnOverlayClick !== null && closeOnOverlayClick !== void 0 ? closeOnOverlayClick : local.closeOnOverlayClick)) {
      if (contentClickRef.current) {
        contentClickRef.current = false;
      } else if (e.target === dialogPosition.current) {
        onOverlayClick === null || onOverlayClick === void 0 ? void 0 : onOverlayClick({
          e: e
        });
        onClose === null || onClose === void 0 ? void 0 : onClose({
          e: e,
          trigger: "overlay"
        });
      }
    }
  };
  var handleCancel = function handleCancel(_ref) {
    var e = _ref.e;
    onCancel === null || onCancel === void 0 ? void 0 : onCancel({
      e: e
    });
    onClose === null || onClose === void 0 ? void 0 : onClose({
      e: e,
      trigger: "cancel"
    });
  };
  var handleClose = function handleClose(_ref2) {
    var e = _ref2.e;
    onClose === null || onClose === void 0 ? void 0 : onClose({
      e: e,
      trigger: "close-btn"
    });
  };
  var handleKeyDown = function handleKeyDown(e) {
    if (e.key === "Escape") {
      e.stopPropagation();
      onEscKeydown === null || onEscKeydown === void 0 ? void 0 : onEscKeydown({
        e: e
      });
      if (closeOnEscKeydown !== null && closeOnEscKeydown !== void 0 ? closeOnEscKeydown : local.closeOnEscKeydown) {
        onClose === null || onClose === void 0 ? void 0 : onClose({
          e: e,
          trigger: "esc"
        });
      }
    } else if (e.key === "Enter" || e.key === "NumpadEnter") {
      e.stopPropagation();
      confirmOnEnter && (onConfirm === null || onConfirm === void 0 ? void 0 : onConfirm({
        e: e
      }));
    }
  };
  var onAnimateLeave = function onAnimateLeave() {
    onClosed === null || onClosed === void 0 ? void 0 : onClosed();
    if (!wrapRef.current) return;
    wrapRef.current.style.display = "none";
  };
  var onAnimateStart = function onAnimateStart() {
    onOpened === null || onOpened === void 0 ? void 0 : onOpened();
    if (!wrapRef.current) return;
    wrapRef.current.style.display = "block";
  };
  var renderMask = function renderMask() {
    if (mode !== "modal") return null;
    return showOverlay ? /* @__PURE__ */React.createElement(CSSTransition, {
      "in": visible,
      appear: true,
      timeout: 300,
      classNames: "".concat(componentCls, "-fade"),
      mountOnEnter: true,
      unmountOnExit: true,
      nodeRef: maskRef
    }, /* @__PURE__ */React.createElement("div", {
      ref: maskRef,
      className: "".concat(componentCls, "__mask")
    })) : null;
  };
  return /* @__PURE__ */React.createElement(CSSTransition, {
    "in": visible,
    appear: true,
    timeout: 300,
    mountOnEnter: true,
    unmountOnExit: destroyOnClose,
    nodeRef: portalRef,
    onEnter: onAnimateStart,
    onExited: onAnimateLeave
  }, /* @__PURE__ */React.createElement(Portal, {
    attach: attach,
    ref: portalRef
  }, /* @__PURE__ */React.createElement("div", {
    ref: wrapRef,
    className: classNames(props.className, "".concat(componentCls, "__ctx"), "".concat(componentCls, "__").concat(mode), (_classNames = {}, _defineProperty(_classNames, "".concat(componentCls, "__ctx--fixed"), !showInAttachedElement), _defineProperty(_classNames, "".concat(componentCls, "__ctx--absolute"), showInAttachedElement), _classNames)),
    style: {
      zIndex: zIndex
    },
    onKeyDown: handleKeyDown,
    tabIndex: 0
  }, renderMask(), /* @__PURE__ */React.createElement("div", {
    className: "".concat(componentCls, "__wrap")
  }, /* @__PURE__ */React.createElement("div", {
    ref: dialogPosition,
    className: classNames("".concat(componentCls, "__position"), (_classNames2 = {}, _defineProperty(_classNames2, "".concat(componentCls, "--top"), !!props.top || props.placement === "top"), _defineProperty(_classNames2, "".concat(componentCls, "--center"), props.placement === "center" && !props.top), _classNames2)),
    style: {
      paddingTop: parseValueToPx(props.top)
    },
    onClick: onMaskClick
  }, /* @__PURE__ */React.createElement(CSSTransition, {
    "in": visible,
    appear: true,
    mountOnEnter: true,
    timeout: 300,
    classNames: "".concat(componentCls, "-zoom"),
    nodeRef: dialogCardRef
  }, /* @__PURE__ */React.createElement(DialogCard, _objectSpread(_objectSpread({
    ref: dialogCardRef
  }, restState), {}, {
    style: _objectSpread(_objectSpread({}, style), {}, {
      width: parseValueToPx(width)
    }),
    onConfirm: onConfirm,
    onCancel: handleCancel,
    onCloseBtnClick: handleClose,
    onMouseDown: onDialogMoveStart
  }))))))));
});
Dialog.displayName = "Dialog";
Dialog.defaultProps = dialogDefaultProps;

export { Dialog as default };
//# sourceMappingURL=Dialog.js.map
